import { useViewModeStore } from '@/stores/viewModeStore';
import { useStoreSelector } from '@/composables/useStoreSelector';
import { useUserAuthStore } from '@/stores/userAuthStore';
import { useAdminCompaniesStore } from '@/stores/adminCompaniesStore';
import { useClientAssetsStore } from '@/stores/clientAssetsStore';
import { useClientCalendarStore } from '@/stores/clientCalendarStore';
import { useClientCompaniesStore } from '@/stores/clientCompaniesStore';
import { useClientProjectsStore } from '@/stores/clientProjectsStore';
import { useClientDeliverablesStore } from '@/stores/clientDeliverablesStore';
import { useClientReviewStore } from '@/stores/clientReviewStore';
import { useClientShowsStore } from '@/stores/clientShowsStore';
import { useClientEpisodesStore } from '@/stores/clientEpisodesStore';
import { useRouter } from 'vue-router';
import { logger } from './loggerService';
import { ref } from 'vue';
import { companyCache } from './companyCache';

export function useImpersonationService(routerInstance = null) {
  const viewModeStore = useViewModeStore();
  const { activeStores } = useStoreSelector();
  const userAuthStore = useUserAuthStore();
  const adminCompaniesStore = useAdminCompaniesStore();
  const clientAssetsStore = useClientAssetsStore();
  const clientCalendarStore = useClientCalendarStore();
  const clientCompaniesStore = useClientCompaniesStore();
  const clientProjectsStore = useClientProjectsStore();
  const clientDeliverablesStore = useClientDeliverablesStore();
  const clientReviewStore = useClientReviewStore();
  const clientShowsStore = useClientShowsStore();
  const clientEpisodesStore = useClientEpisodesStore();
  const router = routerInstance || useRouter();

  // Add loading state
  const isTransitioning = ref(false);

  // Function to update theme based on company
  const updateTheme = (company) => {
    if (!company?.brandColor) {
      // Clear theme cache and reset to default
      companyCache.clearTheme();
      logger.info("[Impersonation] Removed theme override");
      return;
    }

    // Use the cache service to apply and cache theme
    companyCache.setAndApplyTheme(company.id, company);
    logger.info("[Impersonation] Applied company theme via cache", {
      companyName: company.name,
      brandColor: company.brandColor,
    });
  };

  // Update client route check
  const isClientRoute = (route) => {
    return route.path.startsWith('/accounts/');
  };

  // Add function to handle route changes
  const handleRouteChange = async (to) => {
    if (viewModeStore.isImpersonating && !isClientRoute(to)) {
      logger.info('[Impersonation] Stopping impersonation - navigating to admin route', { route: to.path });
      await stopImpersonation(to);
    }
  };

  // Function to ensure admin data is loaded
  const ensureAdminData = async () => {
    if (userAuthStore.user && ['admin', 'super'].includes(userAuthStore.user.type)) {
      if (!adminCompaniesStore.companies.length) {
        logger.info('[Impersonation] Loading admin companies data');
        await adminCompaniesStore.init();
      }
    }
  };

  // Function to reset all client stores
  const resetClientStores = async () => {
    logger.info('[Impersonation] Resetting all client stores');

    // Explicitly reset every client store
    const allClientStores = [
      clientCompaniesStore,
      clientProjectsStore,
      clientAssetsStore,
      clientCalendarStore,
      clientDeliverablesStore,
      clientReviewStore,
      clientShowsStore,
      clientEpisodesStore
    ];

    const resetPromises = allClientStores.map(store => {
      if (typeof store.reset === 'function') {
        logger.debug('[Impersonation] Resetting store:', { storeName: store.$id });
        return store.reset();
      }
      return Promise.resolve();
    });

    // Wait for all resets to complete
    await Promise.all(resetPromises);
    logger.info('[Impersonation] All client stores reset successfully');
  };

  // Function to force reload assets for a specific company
  const forceReloadAssets = async (companyId) => {
    logger.info('[Impersonation] Force reloading assets for company', { companyId });

    try {
      // Force initialization with fresh data
      await clientAssetsStore.init(companyId, true);
      logger.info('[Impersonation] Assets force reloaded successfully', {
        companyId,
        assetCount: clientAssetsStore.items.length
      });
    } catch (error) {
      logger.error('[Impersonation] Failed to force reload assets:', error);
      throw error;
    }
  };

  // Force reload all client stores for a specific company (used by router guard)
  const forceReloadClientStores = async (companyId) => {
    logger.info('[Impersonation] Force reloading all client stores for company', { companyId });

    try {
      await Promise.all([
        activeStores.value.projectStore.getCompanyProjects(companyId),
        clientCalendarStore.init(companyId),
        clientDeliverablesStore.init(companyId),
        clientReviewStore.init(companyId),
        clientAssetsStore.init(companyId, true)
      ]);
      logger.info('[Impersonation] All client stores reloaded successfully', { companyId });
    } catch (error) {
      logger.error('[Impersonation] Failed to force reload client stores:', error);
      throw error;
    }
  };

  const startImpersonation = async (companyId) => {
    if (!companyId) {
      logger.error('[Impersonation] Attempted to start impersonation without company ID');
      throw new Error('Company ID is required for impersonation');
    }

    if (!userAuthStore.user || !['admin', 'super'].includes(userAuthStore.user.type)) {
      throw new Error('Only admin users can access company portals');
    }

    logger.info('[Impersonation] Starting company impersonation', { companyId });
    try {
      // If already impersonating, properly clean up first
      if (viewModeStore.isImpersonating) {
        logger.info('[Impersonation] Already impersonating, cleaning up first');
        await resetClientStores();
        viewModeStore.stopImpersonation();
        updateTheme(null);
      }

      // Ensure admin data is loaded before switching modes
      await ensureAdminData();

      // Reset any existing client stores first
      await resetClientStores();

      // Switch to client view mode
      viewModeStore.startImpersonation(companyId);

      // Now we can safely load data into client stores
      const companyData = await activeStores.value.companyStore.getCompany(companyId);
      if (!companyData) {
        throw new Error('Company not found — it may have been deleted');
      }

      // Apply company theme
      updateTheme(companyData);

      // Load projects, calendar, deliverables, and review data in parallel
      await Promise.all([
        activeStores.value.projectStore.getCompanyProjects(companyId),
        clientCalendarStore.init(companyId),
        clientDeliverablesStore.init(companyId),
        clientReviewStore.init(companyId)
      ]);

      // Navigate to dashboard using named route
      return router.push({
        name: 'account-assets',
        params: { id: companyId }
      });
    } catch (error) {
      // If anything fails, stop impersonation and reset view mode
      viewModeStore.stopImpersonation();
      // Reset theme
      updateTheme(null);
      logger.error('[Impersonation] Failed to start impersonation:', {
        error: error.message,
        companyId
      });
      throw error;
    }
  };

  const stopImpersonation = async (targetRoute = null) => {
    if (!viewModeStore.isImpersonating) return;

    const impersonatedCompanyId = viewModeStore.impersonatedCompany;
    // Land on the company you were just inside, not the top of the list.
    const to =
      targetRoute ||
      (impersonatedCompanyId
        ? { name: 'admin-companies-details', params: { id: impersonatedCompanyId } }
        : { name: 'admin-companies' });
    logger.info('[Impersonation] Stopping impersonation', {
      route: targetRoute ? (targetRoute.name || targetRoute.path) : to.name
    });

    try {
      // Set loading state immediately
      isTransitioning.value = true;

      // Clean up impersonation state before navigating
      // to prevent the router guard from racing with this function
      await resetClientStores();
      viewModeStore.stopImpersonation();
      updateTheme(null);

      // Remove the impersonated company from cache so its logo
      // doesn't bleed onto the login page via getMostRecentCompany()
      if (impersonatedCompanyId) {
        companyCache.removeCompany(impersonatedCompanyId);
      }

      // Reload admin data if needed
      if (userAuthStore.user && ['admin', 'super'].includes(userAuthStore.user.type)) {
        await Promise.all([
          activeStores.value.companyStore.init(),
          activeStores.value.projectStore.init()
        ]);
      }

      // Navigate to admin route after cleanup is complete
      router.push(to);
    } catch (error) {
      logger.error('[Impersonation] Failed to stop impersonation:', error);
      throw error;
    } finally {
      isTransitioning.value = false;
    }
  };

  const switchImpersonation = async (companyId) => {
    if (!companyId) {
      logger.error('[Impersonation] Attempted to switch impersonation without company ID');
      throw new Error('Company ID is required for switching impersonation');
    }

    if (!userAuthStore.user || !['admin', 'super'].includes(userAuthStore.user.type)) {
      throw new Error('Only admin users can access company portals');
    }

    const previousCompanyId = viewModeStore.impersonatedCompany;
    logger.info('[Impersonation] Switching company impersonation', { companyId });
    try {
      // Ensure admin data is loaded
      await ensureAdminData();

      // Reset all client stores first
      await resetClientStores();

      // Stop current impersonation
      viewModeStore.stopImpersonation();

      // Reset theme and clear previous company's cache
      updateTheme(null);
      if (previousCompanyId && previousCompanyId !== companyId) {
        companyCache.removeCompany(previousCompanyId);
      }

      // Start new impersonation
      viewModeStore.startImpersonation(companyId);

      // Load company data
      const companyData = await activeStores.value.companyStore.getCompany(companyId);
      if (!companyData) {
        throw new Error('Failed to load company data');
      }

      // Apply company theme
      updateTheme(companyData);

      // Load projects, calendar, deliverables, and review data in parallel
      await Promise.all([
        activeStores.value.projectStore.getCompanyProjects(companyId),
        clientCalendarStore.init(companyId),
        clientDeliverablesStore.init(companyId),
        clientReviewStore.init(companyId)
      ]);

      // Navigate to dashboard using named route
      return router.push({
        name: 'account-assets',
        params: { id: companyId }
      });
    } catch (error) {
      // If anything fails, stop impersonation and reset view mode
      viewModeStore.stopImpersonation();
      // Reset theme
      updateTheme(null);
      logger.error('[Impersonation] Failed to switch impersonation:', {
        error: error.message,
        companyId
      });
      throw error;
    }
  };

  // Function to initialize service state
  const initializeService = async () => {
    if (viewModeStore.isImpersonating) {
      logger.info('[Impersonation] Initializing service during active impersonation');
      await ensureAdminData();
    }
  };

  // Don't auto-initialize - let consumers call this when needed

  return {
    startImpersonation,
    stopImpersonation,
    switchImpersonation,
    handleRouteChange,
    isClientRoute,
    updateTheme,
    ensureAdminData,
    resetClientStores,
    forceReloadAssets,
    forceReloadClientStores,
    initializeService,
    isTransitioning
  };
} 